import React, { useCallback, useState } from 'react'
import type { Editor } from '@tiptap/react'
import { TextStyleKit } from '@tiptap/extension-text-style'
import { EditorContent, mergeAttributes, useEditor, useEditorState } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { BoldIcon } from '../tiptap-icons/bold-icon'
import { ItalicIcon } from '../tiptap-icons/italic-icon'
import { StrikeIcon } from '../tiptap-icons/strike-icon'
import { Code2Icon } from '../tiptap-icons/code2-icon'
import { SubscriptIcon } from '../tiptap-icons/subscript-icon'
import { HeadingOneIcon } from '../tiptap-icons/heading-one-icon'
import { HeadingThreeIcon } from '../tiptap-icons/heading-three-icon'
import { HeadingTwoIcon } from '../tiptap-icons/heading-two-icon'
import { List } from 'immutable'
import { ListIcon } from '../tiptap-icons/list-icon'
import { ListOrderedIcon } from '../tiptap-icons/list-ordered-icon'
import { CodeBlockIcon } from '../tiptap-icons/code-block-icon'
import { BlockquoteIcon } from '../tiptap-icons/blockquote-icon'
import { Undo2Icon } from '../tiptap-icons/undo2-icon'
import { Redo2Icon } from '../tiptap-icons/redo2-icon'
import '../styles/tiptap.css'
import TextAlign from '@tiptap/extension-text-align'
import { AlignCenterIcon } from '../tiptap-icons/align-center-icon'
import { AlignLeftIcon } from '../tiptap-icons/align-left-icon'
import { AlignRightIcon } from '../tiptap-icons/align-right-icon'
import Underline from '@tiptap/extension-underline'
import { UnderlineIcon } from '../tiptap-icons/underline-icon'
import Paragraph from '@tiptap/extension-paragraph';
import ColorPickerButton from './TextColorMenu'
import { Color, TextStyle } from '@tiptap/extension-text-style'
import Image from '@tiptap/extension-image'
import Modal from 'react-modal';
import Client from '../../models/client'
import ImageModal from './ImageModal'

Modal.setAppElement('#app');

// define your extension array
const extensions = [
    StarterKit,
    TextStyleKit,
    TextAlign.configure({
        types: ['heading', 'paragraph'],
    }),
    Paragraph.extend({
        parseHTML() {
            return [{ tag: 'div' }]
        },
        renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, any> }) {
            return ['div', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0]
        },
    }),
    Underline,
    Color,
    Image,
    Image.configure({
        inline: true,
    }),
]

type Props = {
    client: Client,
    value: string
    onChange: (value: string) => void
}

function MenuBar({ editor, client }: { editor: Editor, client: Client }) {
    // Read the current editor's state, and re-render the component when it changes
    const editorState = useEditorState({
        editor,
        selector: (ctx) => {
            return {
                isBold: ctx.editor.isActive('bold') ?? false,
                canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
                isItalic: ctx.editor.isActive('italic') ?? false,
                canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
                isStrike: ctx.editor.isActive('strike') ?? false,
                canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
                isCode: ctx.editor.isActive('code') ?? false,
                canCode: ctx.editor.can().chain().toggleCode().run() ?? false,
                canClearMarks: ctx.editor.can().chain().unsetAllMarks().run() ?? false,
                isParagraph: ctx.editor.isActive('paragraph') ?? false,
                isHeading1: ctx.editor.isActive('heading', { level: 1 }) ?? false,
                isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
                isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
                isHeading4: ctx.editor.isActive('heading', { level: 4 }) ?? false,
                isHeading5: ctx.editor.isActive('heading', { level: 5 }) ?? false,
                isHeading6: ctx.editor.isActive('heading', { level: 6 }) ?? false,
                isBulletList: ctx.editor.isActive('bulletList') ?? false,
                isOrderedList: ctx.editor.isActive('orderedList') ?? false,
                isCodeBlock: ctx.editor.isActive('codeBlock') ?? false,
                isBlockquote: ctx.editor.isActive('blockquote') ?? false,
                canUndo: ctx.editor.can().chain().undo().run() ?? false,
                canRedo: ctx.editor.can().chain().redo().run() ?? false,

                isAlignLeft: ctx.editor.isActive({ textAlign: 'left' }) ?? false,
                isAlignCenter: ctx.editor.isActive({ textAlign: 'center' }) ?? false,
                isAlignRight: ctx.editor.isActive({ textAlign: 'right' }) ?? false,
                isUnderline: ctx.editor.isActive('underline') ?? false,
            }
        },
    })

    const iconWidth = 16

    return (
        <div className="control-group">
            <div className="button-group">
                <button
                    type="button"
                    onClick={() =>
                        editor.chain().focus().toggleHeading({ level: 1 }).run()
                    }
                    className={editorState.isHeading1 ? 'is-active' : ''}>
                    <HeadingOneIcon width={iconWidth} />
                </button>
                <button
                    type="button"
                    onClick={() =>
                        editor.chain().focus().toggleHeading({ level: 2 }).run()
                    }
                    className={editorState.isHeading2 ? 'is-active' : ''}>
                    <HeadingTwoIcon width={iconWidth} />
                </button>
                <button
                    type="button"
                    onClick={() =>
                        editor.chain().focus().toggleHeading({ level: 3 }).run()
                    }
                    className={editorState.isHeading3 ? 'is-active' : ''}>
                    <HeadingThreeIcon width={iconWidth} />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    disabled={!editorState.canBold}
                    className={editorState.isBold ? 'is-active' : ''}>
                    <BoldIcon width={iconWidth} />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    disabled={!editorState.canItalic}
                    className={editorState.isItalic ? 'is-active' : ''}>
                    <ItalicIcon width={iconWidth} />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    disabled={!editorState.canStrike}
                    className={editorState.isStrike ? 'is-active' : ''}>
                    <StrikeIcon width={iconWidth} />
                </button>

                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={editorState.isBulletList ? 'is-active' : ''}>
                    <ListIcon width={iconWidth} />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={editorState.isOrderedList ? 'is-active' : ''}>
                    <ListOrderedIcon width={iconWidth} />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    className={editorState.isBlockquote ? 'is-active' : ''}>
                    <BlockquoteIcon width={iconWidth} />
                </button>

                <button
                    type="button"
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    className={editorState.isAlignLeft ? 'is-active' : ''}
                >
                    <AlignLeftIcon width={iconWidth} />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    className={editorState.isAlignCenter ? 'is-active' : ''}
                >
                    <AlignCenterIcon width={iconWidth} />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    className={editorState.isAlignRight ? 'is-active' : ''}
                >
                    <AlignRightIcon width={iconWidth} />
                </button>

                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    className={editorState.isUnderline ? 'is-active' : ''}
                >
                    <UnderlineIcon width={iconWidth} />
                </button>

                {editor && <ColorPickerButton editor={editor} />}

                {editor && <ImageModal client={client} editor={editor} />}

                <button
                    type="button"
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editorState.canUndo}>
                    <Undo2Icon width={iconWidth} />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editorState.canRedo}>
                    <Redo2Icon width={iconWidth} />
                </button>
            </div>
        </div>
    )
}

const InsertButton = ({ editor, content, label }: { editor: Editor, content: string, label: string }) => {
    // editorまたは挿入内容が存在しない場合はボタンを無効化
    if (!editor || !content) {
        return null;
    }

    const handleInsert = useCallback(() => {
        // 受け取った content props を挿入
        editor.chain()
            // 現在のカーソル位置に content を挿入
            .insertContent(content)
            .run();
    }, [editor, content]); // content が変わる可能性があるので依存配列に含める

    return (
        <button type="button" onClick={handleInsert} disabled={!editor} style={{ margin: '5px', padding: '8px' }}>
            {/* ボタンのラベルも props から受け取る */}
            {label || '文字列を挿入'}
        </button>
    );
};

const EnduserEmailTiptap = ({ value, onChange, client }: Props) => {
    const editor = useEditor({
        extensions,
        content: value,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML())
        },
    })

    return (
        <div style={{ border: '1px solid #ccc' }}>
            <MenuBar editor={editor} client={client} />
            <EditorContent editor={editor} className="custom-editor" />
            <div style={{ borderTop: '1px solid #ccc', marginTop: "0px" }}></div>
            <div>
                <div style={{ margin: "5px" }}>
                    変数挿入
                </div>
                <div>
                    <InsertButton
                        editor={editor}
                        content="{{ inviter_name }}"
                        label="紹介者の名前"
                    />
                    <InsertButton
                        editor={editor}
                        content="{{ guest_name }}"
                        label="ゲストの名前"
                    />
                    <InsertButton
                        editor={editor}
                        content="{{ company_name }}"
                        label="会社名"
                    />
                    <InsertButton
                        editor={editor}
                        content="{{ from_count }}"
                        label="紹介数"
                    />
                    <InsertButton
                        editor={editor}
                        content="{{ invitation_code }}"
                        label="紹介者コード"
                    />

                    <InsertButton
                        editor={editor}
                        content="{{ shop_name }}"
                        label="店舗名"
                    />

                    <InsertButton
                        editor={editor}
                        content="{{ inviter_shop_name }}"
                        label="紹介者の店舗名"
                    />

                    <InsertButton
                        editor={editor}
                        content="{{ cv_report_count }}"
                        label="CV数"
                    />

                    <InsertButton
                        editor={editor}
                        content="{{ cv_report_user_count }}"
                        label="CVユーザー数"
                    />

                    <InsertButton
                        editor={editor}
                        content="{{ coming_report_count }}"
                        label="来店数"
                    />

                    <InsertButton
                        editor={editor}
                        content="{{ coming_report_user_count }}"
                        label="来店ユーザー数"
                    />
                </div>

                <div>
                    <InsertButton
                        editor={editor}
                        content="{{ coming_mail }}"
                        label="ゲストのメールアドレス（来店）"
                    />

                    <InsertButton
                        editor={editor}
                        content="{{ coming_tel }}"
                        label="ゲストの電話番号（来店）"
                    />

                    <InsertButton
                        editor={editor}
                        content="{{ coming_address }}"
                        label="ゲストの住所（来店）"
                    />

                    <InsertButton
                        editor={editor}
                        content="{{ coming_other }}"
                        label="ゲストの会員番号（来店）"
                    />
                </div>
            </div>
        </div>
    )
}

export default EnduserEmailTiptap
